/* eslint-disable react-hooks/exhaustive-deps */
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { formatDateForInput } from 'utilities/common';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import PropTypes from 'prop-types';
import { CreateExpenseCodeSearchInfo, getExpenseCategoryId, getExpenseCategoryTitle } from 'modules/common/expenseCodeHelper';

const EXCEPTION_TYPE_IDS = {
  BENEFIT_EXTENSION: '9F6A7C8B-3D41-4C2A-9E8F-1B7D5A6C4E21',
  NO_COST_EXCEPTION: 'E2B94F17-6A5D-43C8-8F31-0A9D7C4B2E56',
  EXCEPTION: '4A3D9F62-5C18-47B1-AE92-6F8D0C1B5E34'
};

const safeNumber = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Values set by the client's approval or derived from other fields: never focusable or editable, and only
// greyed out when the caller disables them along with the rest of the form
const ReadOnlyField = ({ adornment, ...props }) => (
  <TextField
    margin="dense"
    variant="standard"
    fullWidth
    InputLabelProps={{ shrink: true }}
    InputProps={{
      readOnly: true,
      startAdornment: adornment ? <InputAdornment position="start">{adornment}</InputAdornment> : null
    }}
    inputProps={{ tabIndex: -1 }}
    sx={{ pointerEvents: 'none' }}
    {...props}
  />
);

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  adornment: PropTypes.node
};

export const getMuiMultiSelectValue = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string')
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  return [];
};

const ExceptionComment = props => {
  const {
    enumerations,
    commentFormData,
    clientContactOptions = [],
    savedExceptionApprovers = [],
    expenseCodes = [],
    setCommentFormData,
    isExceptionSentToClient
  } = props;

  // Only contacts with exception approval routing can be picked as approvers
  const clientExceptionApprovers = clientContactOptions.filter(c => c.isExceptionApprover);
  const getContactTitle = id => clientContactOptions.find(c => c.id === id)?.title || id;

  const exceptionTypeOptions =
    enumerations && Array.isArray(enumerations.exceptionTypes)
      ? (enumerations?.exceptionTypes || []).filter(x => x?.isActive !== false)
      : [];

  const exceptionRequestTypeOptions = useMemo(() => {
    const requestTypes = enumerations?.exceptionRequestTypes || [];
    const activeRequestTypes = requestTypes.filter(x => x?.isActive !== false);
    const initialInactiveRequestType = commentFormData?.requestTypeId
      ? requestTypes.find(x => x?.isActive === false && isIdEqual(x?.id, commentFormData.requestTypeId))
      : null;

    return initialInactiveRequestType ? [...activeRequestTypes, initialInactiveRequestType] : activeRequestTypes;
  }, [commentFormData?.commentId]);

  const exceptionStatusLabel =
    (enumerations?.exceptionStatuses || []).find(x => isIdEqual(x?.id, commentFormData?.statusId))?.title || '';

  const expenseCodeSearchInfo = useMemo(() => CreateExpenseCodeSearchInfo(expenseCodes), [expenseCodes]);

  const getExpenseCodeById = expenseCodeId =>
    expenseCodes.find(expenseCode => String(expenseCode?.expenseCodeId) === String(expenseCodeId));

  const selectedExpenseCode = getExpenseCodeById(commentFormData?.expenseCodeId);
  const selectedExpenseCategory = selectedExpenseCode?.category;
  const selectedExpenseCategoryLabel = getExpenseCategoryTitle(selectedExpenseCategory) || '';

  const exceptionTypeId = commentFormData?.exceptionTypeId || ZERO_GUID;

  let isBenefitExtension = isIdEqual(exceptionTypeId, EXCEPTION_TYPE_IDS.BENEFIT_EXTENSION);
  let isException = isIdEqual(exceptionTypeId, EXCEPTION_TYPE_IDS.EXCEPTION);

  let showRequestedAmount = isException;
  let showRequestedItem = isBenefitExtension;

  const actualCost = useMemo(() => {
    const items = commentFormData?.exceptionLineItems || [];
    return items.reduce((sum, li) => sum + safeNumber(li?.totalAmount ?? li?.totalAmount), 0);
  }, [commentFormData?.exceptionLineItems]);

  const approvedAmount = useMemo(() => {
    const items = commentFormData?.exceptionLineItems || [];
    return items.reduce((sum, li) => sum + safeNumber(li?.approvedAmount ?? li?.approvedAmount), 0);
  }, [commentFormData?.exceptionLineItems]);

  const exceptionApprovers = commentFormData?.exceptionApprovers || [];

  const selectedApproverIds1 = exceptionApprovers
    .filter(x => x?.exceptionGroupNumber === 1)
    .map(x => x?.exceptionApproverId)
    .filter(Boolean);

  const selectedApproverIds2 = exceptionApprovers
    .filter(x => x?.exceptionGroupNumber === 2)
    .map(x => x?.exceptionApproverId)
    .filter(Boolean);

  const selectedSet1 = new Set(selectedApproverIds1);
  const selectedSet2 = new Set(selectedApproverIds2);

  // A saved approver who has since lost exception approval routing stays listed in their own group so they can be
  // removed, but can't be picked again; contacts without routing who weren't already approvers aren't listed
  const getApproverOptions = (groupNumber, otherGroupIds) => {
    const savedIneligibleApprovers = savedExceptionApprovers
      .filter(x => x?.exceptionGroupNumber === groupNumber && !clientExceptionApprovers.some(a => a.id === x?.exceptionApproverId))
      .map(x => ({ id: x.exceptionApproverId, title: getContactTitle(x.exceptionApproverId), isIneligible: true }));

    return [...clientExceptionApprovers.filter(a => !otherGroupIds.has(a.id)), ...savedIneligibleApprovers];
  };

  const optionsForApprover1 = getApproverOptions(1, selectedSet2);
  const optionsForApprover2 = getApproverOptions(2, selectedSet1);

  // Ineligible approvers look disabled either way; while checked they stay clickable so they can be unchecked
  const renderApproverOption = (approver, selectedIds) => {
    const isSelected = selectedIds.has(approver.id);
    return (
      <MenuItem
        key={approver.id}
        value={approver.id}
        disabled={approver.isIneligible && !isSelected}
        sx={approver.isIneligible && isSelected ? { color: 'text.disabled' } : undefined}
      >
        {approver.isIneligible ? `${approver.title} (no longer an approver)` : approver.title}
      </MenuItem>
    );
  };

  const setApproverGroupIds = (groupNumber, nextIds) => {
    const withoutGroup = exceptionApprovers.filter(a => a?.exceptionGroupNumber !== groupNumber);
    const nextEntries = (nextIds || []).map(id => ({
      exceptionGroupNumber: groupNumber,
      exceptionApproverId: id
    }));

    setCommentFormData({
      ...commentFormData,
      exceptionApprovers: [...withoutGroup, ...nextEntries]
    });
  };

  useEffect(() => {
    isBenefitExtension = isIdEqual(commentFormData?.exceptionTypeId, EXCEPTION_TYPE_IDS.BENEFIT_EXTENSION);
    isException = isIdEqual(commentFormData?.exceptionTypeId, EXCEPTION_TYPE_IDS.EXCEPTION);

    showRequestedAmount = isException;
    showRequestedItem = isBenefitExtension;
  }, [commentFormData?.exceptionTypeId]);

  const approvedByLabel = commentFormData?.approvedById ? getContactTitle(commentFormData.approvedById) : '';

  return (
    <Grid container>
      {/* Same side padding as the attachments card above, so the two cards line up */}
      <Grid item xs={12} sx={{ alignSelf: 'flex-end', paddingX: '8px' }}>
        <Paper
          sx={{
            padding: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            backgroundColor: 'transparent',
            width: '100%'
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Exception
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <ReadOnlyField label="Status" name="exceptionStatus" value={exceptionStatusLabel} disabled={isExceptionSentToClient} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="exceptionType">Exception Type</InputLabel>
                <Select
                  labelId="exceptionType"
                  value={exceptionTypeId}
                  onChange={e => setCommentFormData({ ...commentFormData, exceptionTypeId: e.target.value })}
                  variant="standard"
                  disabled={isExceptionSentToClient}
                >
                  {(exceptionTypeOptions || []).map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {showRequestedAmount && (
              <Grid item xs={4}>
                <TextField
                  label="Requested $"
                  name="exceptionRequestedMoney"
                  required
                  value={commentFormData?.requestedAmount ?? ''}
                  margin="dense"
                  variant="standard"
                  type="number"
                  InputLabelProps={{ required: false }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  onChange={e => setCommentFormData({ ...commentFormData, requestedAmount: e.target.value })}
                  disabled={isExceptionSentToClient}
                  sx={{ width: '100%' }}
                />
                <Typography variant="caption" display="block" color="primary">
                  Required
                </Typography>
              </Grid>
            )}
            {showRequestedItem && (
              <Grid item xs={4}>
                <TextField
                  label="Requested Item"
                  name="exceptionRequestedItem"
                  required
                  value={commentFormData?.requestedItem ?? ''}
                  margin="dense"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{ required: false }}
                  onChange={e => setCommentFormData({ ...commentFormData, requestedItem: e.target.value })}
                  disabled={isExceptionSentToClient}
                />
                <Typography variant="caption" display="block" color="primary">
                  Required
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Exception Reason"
                name="exceptionReason"
                required
                value={commentFormData?.exceptionReason || ''}
                margin="dense"
                multiline
                rows={1}
                variant="standard"
                fullWidth
                InputLabelProps={{ required: false }}
                onChange={e => setCommentFormData({ ...commentFormData, exceptionReason: e.target.value })}
                disabled={isExceptionSentToClient}
              />
              <Typography variant="caption" display="block" color="primary">
                Required
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="approver1">Approver 1</InputLabel>
                <Select
                  labelId="approver1"
                  multiple
                  variant="standard"
                  value={selectedApproverIds1}
                  onChange={e => {
                    const ids = getMuiMultiSelectValue(e.target.value);
                    setApproverGroupIds(1, ids);
                  }}
                  disabled={isExceptionSentToClient}
                  renderValue={selectedIds => (selectedIds || []).map(getContactTitle).join(', ')}
                >
                  {optionsForApprover1.map(a => renderApproverOption(a, selectedSet1))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="approver2">Approver 2</InputLabel>
                <Select
                  labelId="approver2"
                  multiple
                  variant="standard"
                  value={selectedApproverIds2}
                  onChange={e => {
                    const ids = getMuiMultiSelectValue(e.target.value);
                    setApproverGroupIds(2, ids);
                  }}
                  disabled={isExceptionSentToClient}
                  renderValue={selectedIds => (selectedIds || []).map(getContactTitle).join(', ')}
                >
                  {optionsForApprover2.map(a => renderApproverOption(a, selectedSet2))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ marginTop: '8px' }}>
                <AdvancedSearch
                  labelText="Expense Code"
                  variant="standard"
                  displayValueAccessorKey={expenseCodeSearchInfo.nameKey}
                  saveIdValueAccessorKey={expenseCodeSearchInfo.idKey}
                  searchingCriteria={expenseCodeSearchInfo.searchingColumns}
                  searchingCriteriaExtensions={expenseCodeSearchInfo.searchingColumnExtensions}
                  searchingRows={expenseCodeSearchInfo.searchingRows}
                  id={selectedExpenseCode?.expenseCodeId || commentFormData?.expenseCodeId || ''}
                  value={selectedExpenseCode?.expenseCodeCode || ''}
                  disabled={isExceptionSentToClient}
                  setIdValue={expenseCodeId => {
                    const nextExpenseCodeId = Array.isArray(expenseCodeId) ? null : expenseCodeId;
                    const nextExpenseCode = getExpenseCodeById(nextExpenseCodeId);
                    setCommentFormData({
                      ...commentFormData,
                      expenseCodeId: nextExpenseCodeId,
                      categoryId: nextExpenseCode ? getExpenseCategoryId(nextExpenseCode.category) : null
                    });
                  }}
                  maxWidthsx="360px"
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyField
                label="Category"
                name="exceptionCategory"
                value={selectedExpenseCategoryLabel}
                disabled={isExceptionSentToClient}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="requestType">Request Type</InputLabel>
                <Select
                  labelId="requestType"
                  value={commentFormData?.requestTypeId || ''}
                  onChange={e => setCommentFormData({ ...commentFormData, requestTypeId: e.target.value })}
                  variant="standard"
                  disabled={isExceptionSentToClient}
                >
                  {exceptionRequestTypeOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id} disabled={opt?.isActive === false}>
                      {opt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyField label="Approved By" name="approvedBy" value={approvedByLabel} disabled={isExceptionSentToClient} />
            </Grid>
            <Grid item xs={4}>
              {/* Text rather than a date input, so an empty value doesn't show an editable date mask and picker */}
              <ReadOnlyField
                label="Approved Date"
                name="exceptionApprovedDate"
                value={formatDateForInput(commentFormData?.approvedDate)}
                disabled={isExceptionSentToClient}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyField
                label="Approved Amount"
                name="exceptionApprovedAmount"
                value={approvedAmount || 0}
                adornment="$"
                disabled={isExceptionSentToClient}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyField
                label="Actual Cost"
                name="exceptionActualCost"
                value={actualCost || 0}
                adornment="$"
                disabled={isExceptionSentToClient}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyField
                label="Balance"
                name="balance"
                value={(actualCost || 0) - (approvedAmount || 0)}
                adornment="$"
                disabled={isExceptionSentToClient}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = state => ({});

export default compose(connect(mapStateToProps, {}))(ExceptionComment);